import React, { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import ModalBody from '../../ModalBody';
import CustomToggle from '../../../CustomToggle';
import CustomButton from '../../../CustomButton';
import DropDown from '../../../DropDown';
import CustomTextInput from '../../../CustomTextInput';
import { useTranslation } from 'react-i18next';
import { Quarry } from '../../../../services/apiSLTP/sites/quarries';
import { crudStatus } from '../../../../utils/enums';
import { getTextColor } from '../../../../utils/colors';
import ColorModal from '../../ColorModal';
import { useCreateQuarryMutation, useUpdateQuarryMutation } from '../../../../hooks/mutations/useQuarryMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import ToggleLine from '../../../ToggleLine';

interface ModalProps {
    quarry: Quarry | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateQuarryModal: React.FC<ModalProps> = ({ quarry, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(quarry?.designation || '');
    const [selectedAddress, setSelectedAddress] = useState(quarry?.address || '');
    const [crudStatusState, setCrudStatusState] = useState(quarry?.crudStatus || crudStatus.OK);

    const { user } = useAuth();

    const {
        mutate: mutateQuarry,
        isSuccess: isSuccessQuarry,
        isError: isErrorQuarry,
        isLoading: isLoadingQuarry,
        error: errorQuarry,
        data: dataQuarry,
    } = useUpdateQuarryMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating quarry:', error.message);
        },
    });

    const {
        mutate: addQuarry,
        isSuccess: isSuccessAdded,
        isError: isErrorAdded,
        isLoading: isLoadingAdded,
        error: errorAdded,
        data: dataAdded,
    } = useCreateQuarryMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error adding quarry:', error.message);
        },
    });

    const handleDesignationChange = (text: string) => {
        setDesignation(text);
    };

    const handleActiveChange = (isActive: boolean) => {
        setCrudStatusState(isActive ? crudStatus.OK : crudStatus.INACTIVE);
    };

    return (
        <ModalBody
            title={t('quarry')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!designation.length}
            handleOk={() => {
                isEditMode
                    ? mutateQuarry({
                          quarryId: quarry?._id || '',
                          data: {
                              designation,
                              crudStatus: crudStatusState,
                              address: selectedAddress,
                          },
                      })
                    : addQuarry({ designation, address: selectedAddress, crudStatus: crudStatusState, createdBy: user?._id });
            }}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={handleDesignationChange} errorText={t('error')} required />

            <ToggleLine checked={crudStatusState === crudStatus.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />

            <CustomTextInput value={selectedAddress} label={t('address')} onChange={(text) => setSelectedAddress(text)} errorText={t('error')} />
        </ModalBody>
    );
};

export default UpdateQuarryModal;
